import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import React, { useCallback } from "react"

import { FilterChip } from "@/components/common/Filters"
import { ManagerChips } from "@/components/common/Filters/ManagerChips"
import { StatusChips } from "@/components/common/Filters/StatusChip"
import type { CompanyListFilter, CompanyStatus } from "@/graphql/codegen/graphql"
import { useManagers } from "@/hooks/api/useManagers"
import { TypeChips } from "@/screens/Companies/components/filters/company/TypeChip"
import { CUSTOMER_ACCOUNT_STATUS_LABEL_MAP } from "@/screens/Companies/constants/customerAccountDataMap"
import { COMPANY_EXPLICIT_EMPTY_FILTER_KEYS } from "@/screens/Companies/types/searchParams"
import type { TreeNode } from "@/types/tree"

export const CompanySearchFilterChips: React.FC<{ companyTypes: TreeNode<string>[] }> = ({ companyTypes }) => {
  const navigate = useNavigate({ from: "/app/companies" })
  const { statuses, managers, types, customerAccountStatuses } = useSearch({
    from: "/app/companies/",
    strict: true,
  })

  const { visibleManagers } = useManagers()

  const handleRemoveFilter = useCallback(
    (key: keyof CompanyListFilter, value: string | CompanyStatus) => {
      void navigate({
        search: (prev: CompanyListFilter) => {
          const newFilter = { ...prev }

          if (Array.isArray(newFilter[key])) {
            ;(newFilter[key] as (string | CompanyStatus)[]) = (newFilter[key] as (string | CompanyStatus)[]).filter(
              (item) => item !== value
            )
            // An emptied status filter stays in the URL as an empty selection, or the defaults would come back.
            if (
              (newFilter[key] as (string | CompanyStatus)[]).length === 0 &&
              !COMPANY_EXPLICIT_EMPTY_FILTER_KEYS.includes(key)
            ) {
              delete newFilter[key]
            }
          } else {
            delete newFilter[key]
          }

          if (key === "types") {
            // Clean up customerAccountStatuses on company type filter change
            delete newFilter?.customerAccountStatuses
          }

          return newFilter
        },
        replace: true,
      })
    },
    [navigate]
  )

  if (!statuses?.length && !managers?.length && !types?.length && !customerAccountStatuses?.length) {
    return null
  }

  return (
    <Box data-testid='company-search-filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1'>Filters:</Typography>
      <StatusChips
        statuses={statuses as CompanyStatus[]}
        onRemove={(key, value) => handleRemoveFilter(key as keyof CompanyListFilter, value)}
      />

      {customerAccountStatuses?.map((status, idx) => (
        <FilterChip
          key={`${status}-${idx}`}
          label={`Customer Account Status: ${CUSTOMER_ACCOUNT_STATUS_LABEL_MAP[status]}`}
          onDelete={() => handleRemoveFilter("customerAccountStatuses" as keyof CompanyListFilter, status)}
        />
      ))}

      <ManagerChips managerIds={managers || []} managerOptions={visibleManagers} onRemove={handleRemoveFilter} />
      <TypeChips
        companyTypes={companyTypes}
        selectedTypes={types || []}
        onRemove={(key, value) => handleRemoveFilter(key as keyof CompanyListFilter, value)}
      />
    </Box>
  )
}
